import { Component, OnInit } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService } from 'src/app/core/services';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-job-applications',
  templateUrl: './job-applications.component.html',
  styleUrls: ['./job-applications.component.scss'],
})
export class JobApplicationsComponent implements OnInit {
  applicationList: any[] = [];
  totalDataCount = 0;
  pageSize = 12;
  pageIndex = 1;
  loading = false;
  skipIndex = 0;

  mediaBaseUrl = environment.mediaBaseUrl;

  // Filters
  search = '';
  selectedGender: string | null = null;
  dateRange: Date[] | null = null;

  searchRequest: Subject<string> = new Subject<string>();

  constructor(
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
  ) {}

  ngOnInit(): void {
    this.searchRequest.pipe(debounceTime(1000)).subscribe((value: string) => {
      this.search = value;
      this.resetPagination();
      this.getAllApplications();
    });

    this.getAllApplications();
  }

  /* Build query params */
  private buildParams(sortBy?: any): any {
    const params: any = {
      skip: this.skipIndex,
      limit: this.pageSize,
    };

    if (this.search) params.search = this.search;
    if (this.selectedGender) params.gender = this.selectedGender;

    if (this.dateRange && this.dateRange.length === 2) {
      params.startDate = this.dateRange[0].toISOString();
      params.endDate = this.dateRange[1].toISOString();
    }

    if (sortBy) params.sortBy = JSON.stringify(sortBy);

    return params;
  }

  /* Get all job applications */
  getAllApplications(sortBy?: any): void {
    const params = this.buildParams(sortBy);
    this.loading = true;

    this.httpRequestService
      .request('get', 'job-applications', params)
      .subscribe(
        (result: any) => {
          this.loading = false;
          // Result structure depends on backend, matching support-details style
          this.applicationList = result.data || result;
          this.totalDataCount =
            result.totalCount || this.applicationList.length;
        },
        () => {
          this.loading = false;
        },
      );
  }

  /* Pagination handler */
  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex, sort } = params;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;
    this.skipIndex = pageSize * (pageIndex - 1);

    const currentSort = sort.find((s) => s.value !== null);
    const sortBy = currentSort
      ? { [currentSort.key]: currentSort.value === 'ascend' ? 1 : -1 }
      : undefined;

    this.getAllApplications(sortBy);
  }

  /* Filter handlers */
  onGenderChange(value: string): void {
    this.resetPagination();
    this.getAllApplications();
  }

  onDateRangeChange(dates: Date[]): void {
    this.dateRange = dates;
    this.resetPagination();
    this.getAllApplications();
  }

  searchData(event: any): void {
    this.searchRequest.next(event);
  }

  /* Delete Application */
  deleteApplication(id: string): void {
    this.httpRequestService
      .request('delete', `job-applications/${id}`)
      .subscribe(
        () => {
          this.notificationService.success(
            '',
            'Application deleted successfully',
          );
          this.getAllApplications();
        },
        (error: any) => {
          this.notificationService.error(
            '',
            error?.error?.message || 'Failed to delete application',
          );
        },
      );
  }

  /* Helper: reset pagination */
  private resetPagination(): void {
    this.pageIndex = 1;
    this.skipIndex = 0;
    this.applicationList = [];
  }
}
